import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { timeZones } from '@/lib/constants'
import { cn } from '@/lib/utils'
import type { WorkspaceSettings } from './types'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon, UnfoldMoreIcon } from '@hugeicons/core-free-icons'
import {
  SettingsWell,
  SettingsCard,
  SettingsRow,
  SettingsField,
  SettingsToggle,
  SettingsFooter,
  SettingsDangerAction,
  settingsButtonClass,
  settingsSaveClass,
  settingsDangerOutlineClass,
} from '@/components/shared/settings/SettingsKit'

interface GeneralSettingsTabProps {
  workspaceName: string
  workspaceId?: string
  settings: WorkspaceSettings
  onSettingsChange: (settings: Partial<WorkspaceSettings>) => void
  onNameChange?: (name: string) => void
  onSave: () => void
  isSaving?: boolean
  isDirty?: boolean
  onDeleteWorkspace?: () => Promise<void>
  siteCount?: number
}

export default function GeneralSettingsTab({
  workspaceName,
  workspaceId,
  settings,
  onSettingsChange,
  onNameChange,
  onSave,
  isSaving = false,
  isDirty = false,
  onDeleteWorkspace,
  siteCount = 0,
}: GeneralSettingsTabProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const canDelete = confirmText === workspaceName

  const browserTimezone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }, [])

  const currentTimezoneDisplay = useMemo(() => {
    if (!settings.timezone) return 'Select timezone...'
    const tz = timeZones.find(
      (t) => t.utc.includes(settings.timezone) || t.value === settings.timezone,
    )
    return tz?.text || settings.timezone
  }, [settings.timezone])

  const filteredTimezones = useMemo(() => {
    if (!searchQuery) return timeZones
    const query = searchQuery.toLowerCase()
    return timeZones.filter(
      (tz) =>
        tz.text.toLowerCase().includes(query) ||
        tz.utc.some((u) => u.toLowerCase().includes(query)) ||
        tz.abbr.toLowerCase().includes(query),
    )
  }, [searchQuery])

  const groupedTimezones = useMemo(() => {
    const groups: Record<string, typeof timeZones> = {}
    filteredTimezones.forEach((tz) => {
      const offsetKey = tz.offset >= 0 ? `UTC+${tz.offset}` : `UTC${tz.offset}`
      if (!groups[offsetKey]) groups[offsetKey] = []
      groups[offsetKey].push(tz)
    })
    return groups
  }, [filteredTimezones])

  const unassigned = siteCount > 0 ? ` ${siteCount} site${siteCount !== 1 ? 's' : ''} will be unassigned.` : ''

  const cancelDelete = () => {
    setConfirming(false)
    setConfirmText('')
  }

  return (
    <SettingsWell>
      <SettingsCard>
        <SettingsRow title="Workspace" description="Name and defaults for this workspace">
          <SettingsField label="Workspace name" htmlFor="workspace-name">
            <Input
              id="workspace-name"
              value={workspaceName}
              onChange={(e) => onNameChange?.(e.target.value)}
              placeholder="Workspace name"
              className="h-9 rounded-lg px-3 text-sm"
            />
          </SettingsField>
        </SettingsRow>

        <SettingsRow
          title="Timezone"
          description="Default day boundaries for sites in this workspace. Each site can override it in Site settings."
        >
          <SettingsField label="Timezone">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={settings.useBrowserTimezone}
                  className="h-9 w-full justify-between rounded-lg px-3 text-sm font-normal shadow-none disabled:border-border disabled:bg-muted/50 disabled:opacity-100 disabled:text-foreground/40"
                >
                  <span className="truncate">{currentTimezoneDisplay}</span>
                  <HugeiconsIcon icon={UnfoldMoreIcon} size={12} className="ml-2 shrink-0 text-foreground/30" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search timezone..." value={searchQuery} onValueChange={setSearchQuery} />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No timezone found.</CommandEmpty>
                    {Object.entries(groupedTimezones).map(([offset, tzList]) => (
                      <CommandGroup key={offset} heading={offset}>
                        {tzList.map((tz) => (
                          <CommandItem
                            key={tz.value}
                            value={tz.text}
                            onSelect={() => {
                              onSettingsChange({ timezone: tz.utc[0] })
                              setOpen(false)
                              setSearchQuery('')
                            }}
                            className={cn('cursor-pointer', settings.timezone === tz.utc[0] && 'bg-accent')}
                          >
                            <span className="truncate">{tz.text}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </SettingsField>
          <SettingsToggle
            title="Use browser timezone"
            description={`Detect it from the current browser (${browserTimezone})`}
            checked={settings.useBrowserTimezone}
            onCheckedChange={(checked) => {
              onSettingsChange({
                useBrowserTimezone: checked,
                timezone: checked ? browserTimezone : settings.timezone,
              })
            }}
          />
        </SettingsRow>

        <SettingsFooter>
          <Button onClick={onSave} disabled={isSaving || !isDirty} className={`${settingsSaveClass} gap-1.5`}>
            {isSaving && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </SettingsFooter>
      </SettingsCard>

      {onDeleteWorkspace && workspaceId && (
        <SettingsCard danger>
          <SettingsRow title="Danger zone" description="Irreversible actions for this workspace">
            <SettingsDangerAction
              title="Delete workspace"
              description={`Removes the workspace, team access and settings.${unassigned} Historical data is kept.`}
              action={
                !confirming && (
                  <Button variant="outline" onClick={() => setConfirming(true)} className={settingsDangerOutlineClass}>
                    Delete workspace
                  </Button>
                )
              }
            >
              {confirming && (
                <>
                  <SettingsField label={`Type ${workspaceName} to confirm`} htmlFor="confirm-delete-workspace">
                    <Input
                      id="confirm-delete-workspace"
                      autoFocus
                      autoComplete="off"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={workspaceName}
                      disabled={isDeleting}
                      className="h-9 rounded-lg text-sm"
                    />
                  </SettingsField>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={cancelDelete} disabled={isDeleting} className={settingsButtonClass}>
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!onDeleteWorkspace || !canDelete) return
                        setIsDeleting(true)
                        try {
                          await onDeleteWorkspace()
                          cancelDelete()
                        } finally {
                          setIsDeleting(false)
                        }
                      }}
                      disabled={!canDelete || isDeleting}
                      className={`${settingsButtonClass} gap-1.5 bg-danger text-white hover:bg-danger/90`}
                    >
                      {isDeleting && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
                      {isDeleting ? 'Deleting…' : 'Delete workspace'}
                    </Button>
                  </div>
                </>
              )}
            </SettingsDangerAction>
          </SettingsRow>
        </SettingsCard>
      )}
    </SettingsWell>
  )
}
